import * as THREE from 'three';
import { Entity } from '../entities/Entity.js';
import { CONFIG, ASCII_CHARS } from '../core/config.js';
import { objectPool } from './ObjectPool.js';

/**
 * Particle Effects System for enhanced visual feedback
 */
export class ParticleSystem {
    constructor(scene) {
        this.scene = scene;
        this.particles = [];
        this.initializePools();
    }
    
    initializePools() {
        // Create particle pools for different effect types
        objectPool.createPool(
            'explosion_particle',
            () => this.createParticle('•', 0xff6600),
            (particle, x, y, color) => this.resetExplosionParticle(particle, x, y, color),
            100
        );
        
        objectPool.createPool(
            'pickup_particle',
            () => this.createParticle('◦', 0x00ff88),
            (particle, x, y, color) => this.resetPickupParticle(particle, x, y, color),
            50
        );
        
        objectPool.createPool(
            'death_particle',
            () => this.createParticle('*', 0xff4444),
            (particle, x, y, color) => this.resetDeathParticle(particle, x, y, color),
            200
        );
    }
    
    createParticle(char, color) {
        return new Particle(0, 0, char, color, this.scene);
    }
    
    resetExplosionParticle(particle, x, y, color = 0xff6600) {
        particle.reset(x, y, color);
        particle.velocity = new THREE.Vector3(
            (Math.random() - 0.5) * 15,
            Math.random() * 8 + 2,
            (Math.random() - 0.5) * 15
        );
        particle.lifetime = 0.8 + Math.random() * 0.4;
        particle.fadeRate = 1.5;
        particle.scale = 0.8 + Math.random() * 0.6;
    }
    
    resetPickupParticle(particle, x, y, color = 0x00ff88) {
        particle.reset(x, y, color);
        particle.velocity = new THREE.Vector3(
            (Math.random() - 0.5) * 5,
            Math.random() * 5 + 3,
            (Math.random() - 0.5) * 5
        );
        particle.lifetime = 1.2;
        particle.fadeRate = 1.0;
        particle.scale = 0.6 + Math.random() * 0.4;
    }
    
    resetDeathParticle(particle, x, y, color = 0xff4444) {
        particle.reset(x, y, color);
        particle.velocity = new THREE.Vector3(
            (Math.random() - 0.5) * 8,
            Math.random() * 6 + 1,
            (Math.random() - 0.5) * 8
        );
        particle.lifetime = 1.0 + Math.random() * 0.5;
        particle.fadeRate = 1.2;
        particle.scale = 0.5 + Math.random() * 0.5;
    }
    
    // Effect creation methods
    createExplosion(x, y, intensity = 1) {
        const particleCount = Math.floor(15 * intensity);
        for (let i = 0; i < particleCount; i++) {
            const particle = objectPool.get('explosion_particle', x, y);
            if (particle) {
                this.particles.push(particle);
            }
        }
    }
    
    createPickupEffect(x, y) {
        for (let i = 0; i < 8; i++) {
            const particle = objectPool.get('pickup_particle', x, y);
            if (particle) {
                this.particles.push(particle);
            }
        }
    }
    
    createDeathEffect(x, y, enemyType = 'basic') {
        let particleCount = 10;
        let color = 0xff4444;
        
        // Different effects for different enemy types
        switch(enemyType) {
            case 'mini_boss':
                particleCount = 25;
                color = 0xaa44aa;
                break;
            case 'elite':
                particleCount = 15;
                color = 0xff8844;
                break;
            case 'tank':
                particleCount = 20;
                color = 0xaa4444;
                break;
        }
        
        for (let i = 0; i < particleCount; i++) {
            const particle = objectPool.get('death_particle', x, y, color);
            if (particle) {
                this.particles.push(particle);
            }
        }
    }
    
    createLevelUpEffect(x, y) {
        // Special golden particles for level up
        for (let i = 0; i < 20; i++) {
            const particle = objectPool.get('pickup_particle', x, y, 0xffff00);
            if (particle) {
                // Modify for level up effect
                particle.velocity.y = Math.random() * 10 + 5;
                particle.lifetime = 2.0;
                this.particles.push(particle);
            }
        }
    }
    
    update(deltaTime) {
        for (let i = this.particles.length - 1; i >= 0; i--) {
            const particle = this.particles[i];
            const shouldRemove = particle.update(deltaTime);
            
            if (shouldRemove) {
                this.particles.splice(i, 1);
                // Return particle to appropriate pool
                if (particle.char === '•') {
                    objectPool.release('explosion_particle', particle);
                } else if (particle.char === '◦') {
                    objectPool.release('pickup_particle', particle);
                } else if (particle.char === '*') {
                    objectPool.release('death_particle', particle);
                }
            }
        }
    }
    
    getParticleCount() {
        return this.particles.length;
    }
    
    clear() {
        this.particles.forEach(particle => {
            particle.destroy();
        });
        this.particles.length = 0;
    }
}

/**
 * Individual Particle class
 */
class Particle extends Entity {
    constructor(x, y, char, color, scene) {
        super(x, y, char, color, scene, 0.8);
        
        this.velocity = new THREE.Vector3();
        this.gravity = -20;
        this.lifetime = 1.0;
        this.maxLifetime = 1.0;
        this.fadeRate = 1.0;
        this.initialScale = 1.0;
    }
    
    reset(x, y, color) {
        this.position.set(x, 0, y);
        this.color = color;
        this.opacity = 1.0;
        this.maxLifetime = this.lifetime;
        this.initialScale = this.scale;
        
        // Update mesh if it exists
        if (this.mesh) {
            this.mesh.position.copy(this.position);
            this.mesh.material.opacity = this.opacity;
        }
    }
    
    update(deltaTime) {
        // Physics
        this.velocity.y += this.gravity * deltaTime;
        this.position.add(this.velocity.clone().multiplyScalar(deltaTime));
        
        // Lifetime and fading
        this.lifetime -= deltaTime;
        const lifeProgress = 1 - (this.lifetime / this.maxLifetime);
        
        this.opacity = Math.max(0, 1 - (lifeProgress * this.fadeRate));
        this.scale = this.initialScale * (1 - lifeProgress * 0.3);
        
        // Update mesh
        this.updateMesh();
        
        return this.lifetime <= 0;
    }
    
    updateMesh() {
        if (!this.mesh) return;
        
        this.mesh.position.copy(this.position);
        this.mesh.material.opacity = this.opacity;
        this.mesh.scale.setScalar(this.scale);
    }
} 